import React, { useState } from 'react';
import { Calculator, TrendingUp, TrendingDown, Minus, ShoppingCart, DollarSign, Users, RotateCcw } from 'lucide-react';
import { marketApi } from '../../services/marketApi';
import { formatPrice } from '../../utils/formatters';

const FUEL_OPTIONS = [
  { value: '', label: 'Selectionner...' },
  { value: 'essence', label: 'Essence' },
  { value: 'diesel', label: 'Diesel' },
  { value: 'hybride', label: 'Hybride' },
  { value: 'electrique', label: 'Electrique' },
  { value: 'gpl', label: 'GPL' },
  { value: 'cng', label: 'CNG' },
];

const TRANSMISSION_OPTIONS = [
  { value: '', label: 'Selectionner...' },
  { value: 'manual', label: 'Manuelle' },
  { value: 'automatic', label: 'Automatique' },
];

const VERDICT_CONFIG = {
  good_deal: { label: 'Bonne affaire', className: 'bg-green-500/20 text-green-400 border-green-500/30' },
  fair: { label: 'Prix correct', className: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  overpriced: { label: 'Surpaye', className: 'bg-red-500/20 text-red-400 border-red-500/30' },
};

const inputClass = 'w-full bg-dark-hover border border-dark-border rounded-lg px-3 py-2 text-primary text-sm focus:outline-none focus:border-carmin transition-colors';
const labelClass = 'block text-xs font-medium text-secondary mb-1';

const EstimationTab = () => {
  // Form state
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState('');
  const [mileage, setMileage] = useState('');
  const [fuelType, setFuelType] = useState('');
  const [askingPrice, setAskingPrice] = useState('');
  const [strategy, setStrategy] = useState(0.5);
  const [fixedCosts, setFixedCosts] = useState('');
  // Display-only fields
  const [transmission, setTransmission] = useState('');
  const [powerKw, setPowerKw] = useState('');

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const isFormValid = make.trim() && model.trim() && year && mileage && fuelType;

  const handleEstimate = async () => {
    setLoading(true);
    setError('');
    try {
      const body = {
        make: make.trim(),
        model: model.trim(),
        year: parseInt(year),
        mileage: parseInt(mileage),
        fuel_type: fuelType,
      };
      if (askingPrice) body.asking_price = parseInt(askingPrice);
      if (strategy !== undefined && strategy !== null) body.strategy = parseFloat(strategy);
      if (fixedCosts) body.fixed_costs = parseInt(fixedCosts);

      const data = await marketApi.estimate(body);
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'estimation');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setResult(null);
    setError('');
  };

  const strategyLabel = strategy <= 0.3 ? 'Prudent' : strategy >= 0.7 ? 'Agressif' : 'Equilibre';

  // --- FORM VIEW ---
  if (!result) {
    return (
      <div className="space-y-4">
        <div className="bg-dark-card border border-dark-border rounded-xl p-5 space-y-4">
          <div className="flex items-center gap-2 text-primary font-semibold text-sm">
            <Calculator className="h-4 w-4 text-carmin" />
            Estimation de prix
          </div>

          {/* Row 1: Make / Model */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Marque *</label>
              <input type="text" value={make} onChange={e => setMake(e.target.value)} placeholder="ex: BMW" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Modele *</label>
              <input type="text" value={model} onChange={e => setModel(e.target.value)} placeholder="ex: Serie 3" className={inputClass} />
            </div>
          </div>

          {/* Row 2: Year / Mileage / Fuel */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className={labelClass}>Annee *</label>
              <input type="number" value={year} onChange={e => setYear(e.target.value)} placeholder="ex: 2020" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Kilometrage *</label>
              <input type="number" value={mileage} onChange={e => setMileage(e.target.value)} placeholder="ex: 85000" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Carburant *</label>
              <select value={fuelType} onChange={e => setFuelType(e.target.value)} className={inputClass}>
                {FUEL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>

          {/* Row 3: Transmission / Power (display-only) */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Transmission</label>
              <select value={transmission} onChange={e => setTransmission(e.target.value)} className={inputClass}>
                {TRANSMISSION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Puissance (kW)</label>
              <input type="number" value={powerKw} onChange={e => setPowerKw(e.target.value)} placeholder="ex: 135" className={inputClass} />
            </div>
          </div>

          {/* Row 4: Asking price / Fixed costs */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Prix demande</label>
              <input type="number" value={askingPrice} onChange={e => setAskingPrice(e.target.value)} placeholder="ex: 18000" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Frais fixes</label>
              <input type="number" value={fixedCosts} onChange={e => setFixedCosts(e.target.value)} placeholder="ex: 500" className={inputClass} />
            </div>
          </div>

          {/* Strategy slider */}
          <div>
            <label className={labelClass}>Strategie : {strategyLabel} ({Math.round(strategy * 100)}%)</label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={strategy}
              onChange={e => setStrategy(parseFloat(e.target.value))}
              className="w-full accent-carmin"
            />
            <div className="flex justify-between text-[10px] text-muted mt-0.5">
              <span>Prudent</span>
              <span>Equilibre</span>
              <span>Agressif</span>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* Submit */}
        <button
          onClick={handleEstimate}
          disabled={!isFormValid || loading}
          className="w-full h-11 rounded-lg bg-carmin text-white font-medium text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-carmin/90 transition-colors flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Estimation en cours...
            </>
          ) : (
            <>
              <Calculator className="h-4 w-4" />
              Estimer
            </>
          )}
        </button>
      </div>
    );
  }

  // --- RESULTS VIEW ---
  const { pricing, segment_stats, as24_evaluation } = result;
  const acquisition = pricing?.acquisition;
  const resale = pricing?.resale;
  const marketContext = pricing?.market_context;

  const TrendIcon = ({ direction }) => {
    if (direction === 'hausse') return <TrendingUp className="h-4 w-4 text-green-400" />;
    if (direction === 'baisse') return <TrendingDown className="h-4 w-4 text-red-400" />;
    return <Minus className="h-4 w-4 text-muted" />;
  };

  const trendLabel = (direction) => {
    if (direction === 'hausse') return 'En hausse';
    if (direction === 'baisse') return 'En baisse';
    return 'Stable';
  };

  return (
    <div className="space-y-4">
      {/* Back button */}
      <button
        onClick={handleReset}
        className="flex items-center gap-1.5 text-sm text-muted hover:text-primary transition-colors"
      >
        <RotateCcw className="h-3.5 w-3.5" />
        Nouvelle estimation
      </button>

      {/* Block 1 — Prix d'achat */}
      {acquisition && (
        <div className="bg-dark-card border border-dark-border rounded-xl p-5 space-y-3">
          <div className="flex items-center gap-2 text-primary font-semibold text-sm">
            <ShoppingCart className="h-4 w-4 text-carmin" />
            Prix d'achat
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <span className="text-xs text-muted">Achat max</span>
              <p className="text-lg font-bold text-primary">{formatPrice(acquisition.max_purchase_price)}</p>
            </div>
            <div>
              <span className="text-xs text-muted">Marge de negotiation</span>
              <p className="text-lg font-bold text-primary">{formatPrice(acquisition.negotiation_margin)}</p>
            </div>
          </div>
          {acquisition.deal_verdict && (
            <div className="pt-1">
              <span className={`inline-block text-xs font-medium px-2.5 py-1 rounded-full border ${VERDICT_CONFIG[acquisition.deal_verdict]?.className || 'bg-dark-hover text-muted border-dark-border'}`}>
                {VERDICT_CONFIG[acquisition.deal_verdict]?.label || acquisition.deal_verdict}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Block 2 — Prix de revente */}
      {resale && (
        <div className="bg-dark-card border border-dark-border rounded-xl p-5 space-y-3">
          <div className="flex items-center gap-2 text-primary font-semibold text-sm">
            <DollarSign className="h-4 w-4 text-carmin" />
            Prix de revente
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <span className="text-xs text-muted">Prix optimal</span>
              <p className="text-lg font-bold text-primary">{formatPrice(resale.optimal_price)}</p>
            </div>
            <div>
              <span className="text-xs text-muted">Profit estime</span>
              <p className="text-lg font-bold text-green-400">{formatPrice(resale.estimated_profit)}</p>
            </div>
            <div>
              <span className="text-xs text-muted">Delai estime</span>
              <p className="text-lg font-bold text-primary">{resale.estimated_days_to_sell != null ? `${resale.estimated_days_to_sell}j` : '-'}</p>
            </div>
          </div>
          {resale.price_range && (
            <div className="pt-2 space-y-1.5">
              <span className="text-xs text-muted">Fourchettes de prix</span>
              <div className="grid grid-cols-3 gap-2">
                {['aggressive', 'balanced', 'premium'].map(key => (
                  <div key={key} className="bg-dark-hover rounded-lg px-3 py-2 text-center">
                    <span className="text-[10px] text-muted uppercase tracking-wide block">{key === 'aggressive' ? 'Agressif' : key === 'balanced' ? 'Equilibre' : 'Premium'}</span>
                    <span className="text-sm font-semibold text-primary">{formatPrice(resale.price_range[key])}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Block 3 — Contexte marche */}
      {marketContext && (
        <div className="bg-dark-card border border-dark-border rounded-xl p-5 space-y-3">
          <div className="flex items-center gap-2 text-primary font-semibold text-sm">
            <Users className="h-4 w-4 text-carmin" />
            Contexte marche
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <span className="text-xs text-muted">Concurrents actifs</span>
              <p className="text-lg font-bold text-primary">{marketContext.active_competitors ?? '-'}</p>
            </div>
            <div>
              <span className="text-xs text-muted">Tendance</span>
              <div className="flex items-center gap-1.5 mt-0.5">
                <TrendIcon direction={segment_stats?.trend_direction} />
                <span className="text-sm font-medium text-primary">{trendLabel(segment_stats?.trend_direction)}</span>
              </div>
            </div>
          </div>
          {/* Demand score bar */}
          {marketContext.demand_score != null && (
            <div>
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs text-muted">Score demande</span>
                <span className="text-xs font-medium text-primary">{marketContext.demand_score}/100</span>
              </div>
              <div className="h-2 bg-dark-hover rounded-full overflow-hidden">
                <div
                  className="h-full bg-carmin rounded-full transition-all"
                  style={{ width: `${marketContext.demand_score}%` }}
                />
              </div>
            </div>
          )}
          {marketContext.market_verdict && (
            <p className="text-sm text-secondary">{marketContext.market_verdict}</p>
          )}
        </div>
      )}

      {/* Optional — AS24 evaluation */}
      {as24_evaluation && (
        <div className="bg-dark-card border border-dark-border rounded-xl p-5 space-y-3">
          <div className="flex items-center gap-2 text-primary font-semibold text-sm">
            <Calculator className="h-4 w-4 text-carmin" />
            Evaluation AutoScout24
          </div>
          <AS24Gauge evaluation={as24_evaluation} />
        </div>
      )}
    </div>
  );
};

/** Price gauge for AS24 evaluation ranges */
const AS24Gauge = ({ evaluation }) => {
  const { price_low, price_median, price_high, price_target } = evaluation;
  if (!price_low || !price_high) return null;

  const range = price_high - price_low;
  const getPosition = (val) => Math.max(0, Math.min(100, ((val - price_low) / range) * 100));

  return (
    <div className="space-y-2">
      <div className="relative h-3 bg-dark-hover rounded-full overflow-hidden">
        <div
          className="absolute inset-y-0 bg-gradient-to-r from-green-500/40 via-carmin/40 to-red-500/40 rounded-full"
          style={{ left: '0%', right: '0%' }}
        />
        {price_median && (
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-white/60"
            style={{ left: `${getPosition(price_median)}%` }}
          />
        )}
        {price_target && (
          <div
            className="absolute -top-0.5 h-4 w-2 bg-carmin rounded-sm border border-white/20"
            style={{ left: `calc(${getPosition(price_target)}% - 4px)` }}
          />
        )}
      </div>
      <div className="flex justify-between text-[10px] text-muted">
        <span>{formatPrice(price_low)}</span>
        {price_median && <span>Median: {formatPrice(price_median)}</span>}
        <span>{formatPrice(price_high)}</span>
      </div>
      {price_target && (
        <p className="text-xs text-secondary">
          Prix cible : <span className="font-semibold text-primary">{formatPrice(price_target)}</span>
        </p>
      )}
    </div>
  );
};

export default EstimationTab;
